// @ts-check

import { createGuid } from "./cryptoHelper";

/**
 * A mapping for every table's field name and default value.
 *
 * Excludes the following fields because they are common to all tables:
 * - `id: string` (uniqueidentifier)
 * - `location_id: string` (uniqueidentifier)
 * - `LOCA_ID: string`
 * - `active: number` (bit)
 * - `created_at: Date` (datetime)
 * - `modified_at: Date` (datetime)
 */
export const defaultValues = /** @type {const} */ ({
  HORN: [
    ["HORN_TOP", 0],
    ["HORN_BASE", 0],
    ["HORN_ORNT", 0],
    ["HORN_INCL", 0],
    ["HORN_REM", ""],
    ["FILE_FSET", ""],
  ],
  FLSH: [
    ["FLSH_TOP", 0],
    ["FLSH_BASE", 0],
    ["FLSH_TYPE", ""],
    ["FLSH_RETN", 0],
    ["FLSH_RETX", 0],
    ["FLSH_COL", ""],
    ["FLSE_REM", ""],
    ["FILE_FSET", ""],
  ],
  HDPH: [
    ["HDPH_ID", null],  // not used
    ["HDPH_TOP", 0],
    ["HDPH_BASE", 0],
    ["HDPH_TYPE", ""],
    ["HDPH_CREW", ""],
    ["HDPH_EXC", ""],
    ["HDPH_SHOR", ""],
    ["HDPH_STAB", ""],
    ["HDPH_DIML", 0],
    ["HDPH_DIMW", 0],
    ["HDPH_DBIT", ""],
    ["HDPH_BCON", ""],
    ["insitu_type", ""],
    ["HDPH_BTYP", ""],
    ["HDPH_BLEN", 0],
    ["HDPH_LOG", ""],
    ["HDPH_LOGD", null],
    ["HDPH_REM", ""],
    ["HDPH_ENV", ""],
    ["HDPH_METH", ""],
    ["HDPH_CONT", ""],
    ["FILE_FSET", ""],
  ],
})

/**
 * @typedef { keyof typeof defaultValues } TableName
 */

/**
 * @type {Record<TableName, () => Array<[string, any]>>}
 */
export const computeValues = {
  HORN: () => [],
  FLSH: () => [],
  HDPH: () => {
    return [
      ["HDPH_STAR", new Date().toISOString()],
      ["HDPH_ENDD", new Date().toISOString()],
    ]
  },
}

/**
 * The names of tables that require passing in the entire hdph row to compute default values.
 * @typedef {'IVAN' | 'IPRG' | 'PMTG' | 'TELE' | 'IMPK' | 'RPLT'} TablesNeedingHdph
 */

/**
 * Functions to compute default values for tables that require passing in the entire hdph row.
 * While we can split this into default and compute, the benefit is minimal.
 *
 * @type {Record<TablesNeedingHdph, (hdph: object) => Array<[string, any]>>}
 */
export const computeValuesHdph = {
  IVAN: (hdph) => [
    ["hdph_id", hdph.id],
    ["IVAN_TOP", hdph.HDPH_TOP],
    ["IVAN_BASE", hdph.HDPH_BASE],
    ["IVAN_DPTH", 0],
    ["IVAN_IVAN", ""],
    ["IVAN_IVAR", ""],
    ["IVAN_DATE", new Date().toISOString()],
  ],
  IPRG: (hdph) => [
    ["hdph_id", hdph.id],
    ["IPRG_TOP", hdph.HDPH_TOP],
    ["IPRG_BASE", hdph.HDPH_BASE],
    ["IPRG_TYPE", ""],
    ["IPRG_IPRM", ""],
    ["IPRG_TESN", ""],
    ["IPRG_STG", 0],
    ["IPRG_PRWL", 0],
    ["IPRG_SWAL", 0],
    ["IPRG_TDIA", 0],
    ["IPRG_SDIA", 0],
    ["IPRG_FLOW", 0],
    ["IPRG_AWL", 0],
    ["IPRG_HEAD", 0],
    ["IPRG_DATE", new Date().toISOString()],
    ["IPRG_REM", ""],
    ["IPRG_ENV", ""],
    ["IPRG_METH", ""],
    ["IPRG_CONT", ""],
    ["IPRG_CRED", ""],
    ["TEST_STAT", ""],
    ["FILE_FSET", ""],
  ],
  PMTG: (hdph) => [
    ["hdph_id", hdph.id],
    ["PMTG_TOP", hdph.HDPH_TOP],
    ["PMTG_BASE", hdph.HDPH_BASE],
    ["PMTG_DPTH", 0],
    ["PMTG_HO", 0],
    ["PMTG_GI", 0],
    ["PMTG_CU", 0],
    ["PMTG_PL", 0],
    ["PMTG_AF", 0],
    ["PMTG_AD", 0],
    ["PMTG_DATE", new Date().toISOString()],
  ],
  TELE: (hdph) => [
    ["hdph_id", hdph.id],
    ["TELE_TOP", hdph.HDPH_TOP],
    ["TELE_BASE", hdph.HDPH_BASE],
    ["TELE_PEN", 0],
  ],
  IMPK: (hdph) => [
    ["hdph_id", hdph.id],
    ["IMPK_TOP", hdph.HDPH_TOP],
    ["IMPK_BASE", hdph.HDPH_BASE],
    ["IMPK_PEN", 0],
  ],
  RPLT: (hdph) => [
    ["hdph_id", hdph.id],
    ["RPLT_TOP", hdph.HDPH_TOP],
    ["RPLT_BASE", hdph.HDPH_BASE],
    ["SAMP_TOP", 0],
    ["SAMP_REF", ""],
    ["SAMP_TYPE", ""],
    ["SAMP_ID", ""],
    ["SPEC_REF", ""],
    ["SPEC_DPTH", 0],
    ["SPEC_DESC", ""],
    ["SPEC_PREP", ""],
    ["RPLT_PLS", 0],
    ["RPLT_PLSI", 0],
    ["RPLT_PLTF", 0],
    ["RPLT_MC", 0],
    ["RPLT_REM", ""],
    ["RPLT_METH", ""],
    ["RPLT_LAB", ""],
    ["RPLT_CRED", ""],
    ["TEST_STAT", ""],
    ["FILE_FSET", ""],
  ],
}

export const defaultValues0Rows = {
  PTIM: [
    ["PTIM_DPTH", 0],
    ["PTIM_CAS", 0],
    ["PTIM_WAT", 0],
    ["PTIM_REM", ""],
    ["FILE_FSET", ""],
  ],
  CDIA: [
    ["CDIA_DPTH", 0],
    ["CDIA_DIAM", 0],
    ["CDIA_TOP", 0],
    ["CDIA_BASE", 0],
    ["CDIA_TYPE", ""],
  ],
  SAMP: [
    ["SAMP_ID", null],  // not used
    ["SAMP_TOP", 0],
    ["SAMP_BASE", 0],
    ["SAMP_REF", ""],
    ["SAMP_TYPE", ""],
    ["SAMP_RECV", 0],
    ["SAMP_UBLO", 0],
    ["SAMP_DTIM", null],  // yes really
    ["SAMP_CONT", ""],
    ["SAMP_PREP", ""],
    ["SAMP_SDIA", 0],
    ["SAMP_WDEP", 0],
    ["SAMP_TECH", ""],
    ["SAMP_MATX", ""],
    ["SAMP_TYPC", ""],
    ["SAMP_WHO", ""],
    ["SAMP_WHY", ""],
    ["SAMP_REM", ""],
    ["SAMP_DESC", ""],
    ["SAMP_DESD", null],  // yes really
    ["SAMP_LOG", ""],
    ["SAMP_COND", ""],
    ["SAMP_CLSS", ""],
    ["SAMP_BAR", 0],
    ["SAMP_TEMP", 0],
    ["SAMP_PRES", 0],
    ["SAMP_FLOW", 0],
    ["SAMP_ETIM", null],  // yes really
    ["SAMP_DURN", null],  // yes really
    ["SAMP_CAPT", ""],
    ["SAMP_LINK", ""],
    ["GEOL_STAT", ""],
    ["FILE_FSET", ""],
    ["SAMP_RECL", 0],
  ],
  ISPT: [
    ["ISPT_TOP", 0],
    ["ISPT_SEAT", 0],
    ["ISPT_MAIN", 0],
    ["ISPT_NPEN", 0],
    ["ISPT_NVAL", 0],
    ["ISPT_INC1", 0],
    ["ISPT_INC2", 0],
    ["ISPT_INC3", 0],
    ["ISPT_INC4", 0],
    ["ISPT_INC5", 0],
    ["ISPT_INC6", 0],
    ["ISPT_PEN1", 0],
    ["ISPT_PEN2", 0],
    ["ISPT_PEN3", 0],
    ["ISPT_PEN4", 0],
    ["ISPT_PEN5", 0],
    ["ISPT_PEN6", 0],
    ["ISPT_TYPE", ""],
    ["ISPT_IPRM", 0],
    ["ISPT_BASE", 0],
    ["ISPT_WAT", ""],
    ["ISPT_REP", ""],
    ["ISPT_CAS", 0],
    ["ISPT_HAM", ""],
    ["ISPT_ERAT", 0],
    ["ISPT_SWP", 0],
    ["ISPT_ROCK", ""],
    ["ISPT_REM", ""],
    ["ISPT_ENV", ""],
    ["ISPT_METH", ""],
    ["ISPT_CRED", ""],
    ["TEST_STAT", ""],
    ["FILE_FSET", ""],
  ],
  MONG: [
    ["MONG_TYPE", ""],
    ["MONG_DIS", 0],
    ["MONG_ID", ""],
    ["PIPE_REF", ""],
    // MONG_DATE in computed
    ["MONG_DETL", ""],
    ["MONG_TRZ", 0],
    ["MONG_BRZ", 0],
    ["MONG_BRGA", 0],
    ["MONG_BRGB", 0],
    ["MONG_BRGC", 0],
    ["MONG_INCA", 0],
    ["MONG_INCB", 0],
    ["MONG_INCC", 0],
    ["MONG_RSCA", ""],
    ["MONG_RSCB", ""],
    ["MONG_RSCC", ""],
    ["MONG_REM", ""],
    ["MONG_CONT", ""],
    ["FILE_FSET", ""],
  ],
  CORE: [
    ["CORE_TOP", 0],
    ["CORE_BASE", 0],
    ["CORE_PREC", 0],
    ["CORE_SREC", 0],
    ["CORE_RQD", 0],
    ["CORE_rawPREC", 0],
    ["CORE_rawSREC", 0],
    ["CORE_rawRQD", 0],
    ["CORE_DIAM", 0],
    ["CORE_DURN", null],  // yes really
    ["CORE_REM", ""],
    ["FILE_FSET", ""],
  ],
  FRAC: [
    ["FRAC_FROM", 0],
    ["FRAC_TO", 0],
    ["FRAC_SET", ""],
    ["FRAC_IMAX", 0],
    ["FRAC_IMIN", 0],
    ["FRAC_FI", ""],
    ["FRAC_REM", ""],
    ["FILE_FSET", ""],
    ["joint_number", 0],
  ],
  GEOL: [
    ["GEOL_LEG", ""],
    ["GEOL_TOP", 0],
    ["GEOL_BASE", 0],
    ["GEOL_DESC", ""],
    ["GEOL_GEOL", 0],
    ["GEOL_GEOL2", ""],
    ["GEOL_STAT", ""],
    ["GEOL_BGS", ""],
    ["GEOL_FORM", ""],
    ["GEOL_REM", ""],
    ["FILE_FSET", ""],
    ["description", ""],
    ["soil_rock", ""],
  ],
  DETL: [
    ["DETL_TOP", 0],
    ["DETL_BASE", 0],
    ["DETL_DESC", ""],
    ["DETL_REM", ""],
    ["soil_rock", ""],
    ["description", ""],
    ["GEOL_ID", null],  // unused
    ["FILE_FSET", ""],
  ],
}

/**
 * @typedef {keyof typeof defaultValues0Rows} TableName0Rows
 */

/**
 * @param {string} hdph_id
 * @returns {Array<[string, any]>}
 */
const simpleHdphId = (hdph_id) => [
  ["hdph_id", hdph_id],
];

/**
 * @type {Record<TableName0Rows, (hdph_id: string) => Array<[string, any]>>}
 */
export const computeValues0Rows = {
  PTIM: () => [
    ["PTIM_DTIM", new Date().toISOString()],
  ],
  CDIA: simpleHdphId,
  SAMP: simpleHdphId,
  ISPT: simpleHdphId,
  MONG: (hdph_id) => [
    ["hdph_id", hdph_id],
    ["MONG_DATE", new Date().toISOString()],
  ],
  CORE: () => [],
  FRAC: () => [],
  GEOL: () => [],
  DETL: () => [],
}

/**
 * For the given {@link TableName}, returns a table row with default values.
 * Runs the corresponding {@link computeValues} function for the table.
 *
 * This just calls to {@link mkDefaultRowGeneric}, but this function ensures
 * that tableName is of type {@link TableName}.
 *
 * @param {TableName} tableName the name of the new table, uppercase
 * @param {string} location_id the unique id of the LOCA table (foreign key for us)
 * @param {string} LOCA_ID the name of the new drillhole
 * @returns {object} the new table row
 */
export const mkDefaultRow = (tableName, location_id, LOCA_ID) => {
  // our computeValues functions don't take hdph_id, but that's fine
  // because JS ignores extra args. so the function types are compatible.
  return mkDefaultRowGeneric(
    location_id, LOCA_ID, defaultValues[tableName], computeValues[tableName], undefined
  )
}

/**
 * Returns a table row with default values. Runs the given compute function. You have to
 * pass in the correct default values for your table and compute function for
 * your table, so this function avoids the need to pass in a table name.
 *
 * You should use {@link mkDefaultRow} instead if you are know the table name is
 * of type {@link TableName}
 *
 * @template T type of the argument required by the compute function
 * @param {string} location_id the unique id of the LOCA table (foreign key for us)
 * @param {string} LOCA_ID the name of the new drillhole
 * @param {ReadonlyArray<Readonly<[string, any]>>} defaults Array of the tuple [string, default-value]
 * @param {(t: T) => Array<[string, any]>} compute
 * @param {T} t argument required by the compute function
 * @returns {object} the new table row
 */
export const mkDefaultRowGeneric = (location_id, LOCA_ID, defaults, compute, t) => {
  // unique id specific to this table
  const id = createGuid();

  const defaultRow = {
    id: id,
    location_id,
    LOCA_ID,
  };

  for (const [fieldName, value] of compute(t)) {
    defaultRow[fieldName] = value;
  }

  for (const [fieldName, defaultValue] of defaults) {
    defaultRow[fieldName] = defaultValue;
  }

  Object.assign(defaultRow, {
    active: 1,
    created_at: new Date().toISOString(),
    modified_at: new Date().toISOString(),
  })

  return defaultRow
}

/**
 * Returns a tree view child for RichTreeView item.children
 *
 * @param {string} location_id the unique id of the LOCA table (foreign key for us)
 * @param {string} LOCA_ID the name of the new drillhole
 * @param {TableName | TableName0Rows | TablesNeedingHdph} tableName the name of the new table, uppercase
 * @returns {{
 *    id: string;
 *    label: string;
 * }}
 */
export function mkTreeViewChild(location_id, LOCA_ID, tableName) {
  return {
    id: `${location_id}|${LOCA_ID}|${tableName}`,
    label: tableName,
  };
}
